/**
 * We use English comments only in this project.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { subscriptionService } from '../services';
import { SubscriptionState, Schedule } from '../types';
import { Address, SubscriptionStatus } from '../types/api';
import apiClient from '../services/api';
import { authService } from '../services';
import UpgradeConfirmationModal from '../components/subscription/UpgradeConfirmationModal';

export interface DeliverySettings {
  time_slots: string[];
  day_of_week: number;
}

export interface Subscription {
  id?: string;
  plan: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  nextDeliveryDate?: string;
  deliverySettings?: DeliverySettings;
  address?: Address;
}

// ContextSubscription is the same as Subscription
export type ContextSubscription = Subscription;

export interface UpgradeInfo {
  subscriptionId: string;
  currentPlan: 'standard' | 'premium';
  targetPlan: 'premium';
  currentPrice: number;
  newPrice: number;
  currency: 'thb' | 'usd';
  isRecurring: boolean;
  serviceType: 'flower' | 'fruit' | 'cat_grass';
  // For one-time subscriptions
  remainingDeliveries?: number;
  upgradeAmount?: number;
  // For recurring subscriptions
  prorationAmount?: number;
  nextBillingDate?: string;
}

export interface SubscriptionContextType {
  subscription: SubscriptionState | null;
  subscriptions: SubscriptionState[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  subscribe: (type: string, address: Address, schedule: Schedule) => Promise<void>;
  renewSubscription: (subscriptionId?: string) => Promise<void>;
  upgradeSubscription: (subscriptionId?: string) => Promise<void>;
  skipDelivery: (deliveryDate: string, subscriptionId?: string) => void;
  updateDeliverySchedule: (schedule: Schedule, subscriptionId?: string) => void;
  updateAddress: (address: Address, subscriptionId?: string) => void;
  fetchSubscriptionData: (subscriptionId?: string, forceRefresh?: boolean) => Promise<void>;
  // Upgrade confirmation related
  upgradeInfo: UpgradeInfo | null;
  showUpgradeConfirmation: boolean;
  upgradeLoading: boolean;
  fetchUpgradePreview: (subscriptionId: string) => Promise<void>;
  confirmUpgrade: () => Promise<void>;
  cancelUpgrade: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

// Mock data for development - enhanced with fruit subscriptions
const MOCK_SUBSCRIPTIONS_DETAILED: SubscriptionState[] = [
  {
    id: 'mock-1',
    status: 'active',
    plan: 'premium',
    serviceType: 'flower',
    isGift: false,
    isRecurring: true,
    remainingDeliveries: 5,
    nextDeliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    price: 599,
    address: {
      id: 'addr-1',
      street: '123 Flower Street, Apt 4B',
      city: 'Bangkok',
      state: 'Bangkok',
      zipCode: '10110',
      country: 'Thailand',
      contactName: 'John Doe',
      contactPhone: '+66 81 234 5678',
      addressLabel: 'HOME'
    },
    deliverySettings: {
      time_slots: ['9:00 AM - 12:00 PM'],
      day_of_week: 6
    }
  },
  {
    id: 'mock-2',
    status: 'active',
    plan: 'standard',
    serviceType: 'fruit',
    isGift: false,
    isRecurring: false,
    remainingDeliveries: 2,
    nextDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
    startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
    price: 399,
    address: {
      id: 'addr-2',
      street: '456 Orchard Road, Unit 12',
      city: 'Phuket',
      state: 'Phuket',
      zipCode: '83000',
      country: 'Thailand',
      contactName: 'Jane Smith',
      contactPhone: '+66 82 345 6789',
      addressLabel: 'OFFICE'
    },
    deliverySettings: {
      time_slots: ['2:00 PM - 5:00 PM'],
      day_of_week: 6
    }
  },
  {
    id: 'mock-3',
    status: 'cancelled',
    plan: 'standard',
    serviceType: 'flower',
    isGift: false,
    isRecurring: true,
    remainingDeliveries: 0,
    nextDeliveryDate: null,
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    price: 399,
    address: {
      id: 'addr-3',
      street: '789 Garden Lane',
      city: 'Chiang Mai',
      state: 'Chiang Mai',
      zipCode: '50000',
      country: 'Thailand',
      contactName: 'Mike Johnson',
      contactPhone: '+66 83 456 7890',
      addressLabel: 'HOME'
    },
    deliverySettings: {
      time_slots: ['9:00 AM - 12:00 PM'],
      day_of_week: 6
    }
  },
  {
    id: 'mock-4',
    status: 'active',
    plan: 'premium',
    serviceType: 'fruit',
    isGift: false,
    isRecurring: false,
    remainingDeliveries: 0,
    nextDeliveryDate: null,
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    price: 599,
    address: {
      id: 'addr-4',
      street: '321 Tropical Avenue',
      city: 'Pattaya',
      state: 'Chonburi',
      zipCode: '20150',
      country: 'Thailand',
      contactName: 'Sarah Wilson',
      contactPhone: '+66 84 567 8901',
      addressLabel: 'SHOP'
    },
    deliverySettings: {
      time_slots: ['10:00 AM - 1:00 PM'],
      day_of_week: 6
    }
  },
  {
    id: 'mock-5',
    status: 'paused',
    plan: 'standard',
    serviceType: 'flower',
    isGift: false,
    isRecurring: true,
    remainingDeliveries: 8,
    nextDeliveryDate: null,
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
    price: 399,
    address: {
      id: 'addr-5',
      street: '555 Rose Street',
      city: 'Bangkok',
      state: 'Bangkok',
      zipCode: '10400',
      country: 'Thailand',
      contactName: 'David Brown',
      contactPhone: '+66 85 678 9012',
      addressLabel: 'HOME'
    },
    deliverySettings: {
      time_slots: ['9:00 AM - 12:00 PM'],
      day_of_week: 6
    }
  }
];

// Enable mock data in development
const USE_MOCK_DATA = import.meta.env.DEV && window.location.search.includes('mock=true');

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionState[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const fetchInProgressRef = useRef(false);
  
  // Upgrade confirmation state
  const [upgradeInfo, setUpgradeInfo] = useState<UpgradeInfo | null>(null);
  const [showUpgradeConfirmation, setShowUpgradeConfirmation] = useState<boolean>(false);
  const [upgradeLoading, setUpgradeLoading] = useState<boolean>(false);

  // Fetch subscription data when component mounts
  useEffect(() => {
    console.log('SubscriptionContext useEffect triggered');
    
    // If using mock data, set it immediately
    if (USE_MOCK_DATA) {
      console.log('SubscriptionContext: Using mock data');
      setSubscriptions(MOCK_SUBSCRIPTIONS_DETAILED);
      setHasFetched(true);
      setIsLoading(false);
      return;
    }
    
    // Skip fetching subscriptions on non-authenticated pages
    const isNonAuthPage = 
      window.location.pathname.includes('/login') ||
      window.location.pathname.includes('/register') ||
      window.location.pathname.includes('/reset-password') ||
      window.location.pathname.includes('/verify-email') ||
      window.location.pathname.includes('/forgot-password') ||
      window.location.pathname === '/' ||
      window.location.pathname.includes('/welcome');
    
    // Public pages that still need subscription data if user is authenticated
    const isPublicPage = 
      window.location.pathname.includes('/flowers') ||
      window.location.pathname === '/home';
      
    // Always fetch on subscription pages
    const isSubscriptionPage = 
      window.location.pathname.includes('/subscriptions');
    
    // Check authentication - only fetch if user is authenticated
    if (!authService.isAuthenticated()) {
      console.log('SubscriptionContext: User not authenticated, skipping fetch');
      setIsLoading(false);
      return;
    }
    
    // Modified condition: fetch data for authenticated users even on public pages
    if (isSubscriptionPage || !isNonAuthPage) {
      console.log('SubscriptionContext: User is authenticated, fetching subscriptions on page:', window.location.pathname);
      fetchSubscriptions();
    } else {
      console.log('SubscriptionContext: Skipping fetch on non-authenticated page:', window.location.pathname);
      setIsLoading(false);
    }
  }, []);

  // Function to fetch all subscriptions
  const fetchSubscriptions = async (forceRefresh: boolean = false) => {
    // If using mock data, return mock data
    if (USE_MOCK_DATA) {
      console.log('SubscriptionContext: Using mock data for fetchSubscriptions');
      setSubscriptions(MOCK_SUBSCRIPTIONS_DETAILED);
      setHasFetched(true);
      setIsLoading(false);
      return;
    }

    // Skip fetch if user is not authenticated
    if (!authService.isAuthenticated()) {
      console.log('SubscriptionContext: User not authenticated, skipping fetchSubscriptions');
      setIsLoading(false);
      return;
    }
    
    console.log('SubscriptionContext: Fetching subscription data', forceRefresh ? '(forced refresh)' : '');
    
    fetchInProgressRef.current = true;
    setIsLoading(true);
    
    try {
      console.log('SubscriptionContext: Calling API getAllSubscriptions()');
      const userSubscriptions = await subscriptionService.getAllSubscriptions(forceRefresh);
      // console.log('SubscriptionContext: API returned subscriptions:', userSubscriptions?.length || 0);
      
      // Convert API subscriptions to SubscriptionState
      const formattedSubscriptions: SubscriptionState[] = userSubscriptions.map(sub => ({
        ...sub,
        status: sub.status as SubscriptionStatus || null,
        plan: sub.plan || null,
        nextDeliveryDate: sub.nextDeliveryDate || null,
        deliverySettings: sub.deliverySettings || null,
        address: sub.address,
        startDate: sub.startDate || null,
        endDate: sub.endDate || null,
        price: null,
        isGift: false,
        isRecurring: sub.isRecurring || false
      }));
      
      setSubscriptions(formattedSubscriptions);
      setHasFetched(true);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setSubscriptions([]);
      setHasFetched(true);
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  };

  // Add a new subscription
  const subscribe = async (type: string, address: Address, schedule: Schedule) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const newSubscription = await subscriptionService.subscribe(type, address, schedule);
      setSubscriptions(prevSubscriptions => [
        ...prevSubscriptions,
        {
          ...newSubscription,
          status: newSubscription.status as SubscriptionStatus || null,
          plan: newSubscription.plan || null,
          nextDeliveryDate: newSubscription.nextDeliveryDate || null,
          deliverySettings: newSubscription.deliverySettings || null,
          address: newSubscription.address ? {
            street: newSubscription.address.street,
            city: newSubscription.address.city,
            state: newSubscription.address.state,
            zipCode: newSubscription.address.zipCode || ''
          } : null,
          startDate: newSubscription.startDate || null,
          endDate: newSubscription.endDate || null,
          price: null,
          isGift: false,
          isRecurring: newSubscription.isRecurring || false
        } as SubscriptionState
      ]);
      return Promise.resolve();
    } catch (err) {
      console.error('Error subscribing:', err);
      setError('Failed to create subscription. Please try again.');
      return Promise.reject(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Renew subscription
  const renewSubscription = async (subscriptionId?: string) => {
    if (!subscriptionId && subscriptions.length > 0) {
      subscriptionId = subscriptions[0].id;
    }
    
    if (!subscriptionId) {
      console.error('No subscription ID provided for renewal');
      return Promise.reject(new Error('No subscription ID provided'));
    }
    
    try {
      // Instead of creating a payment intent directly, redirect to the renewal page
      // where the user can select the renewal duration
      window.location.href = `/renewal/${subscriptionId}`;
      return Promise.resolve();
    } catch (err) {
      console.error('Error navigating to renewal page:', err);
      setError('Failed to navigate to renewal page. Please try again.');
      return Promise.reject(err);
    }
  };

  // Fetch upgrade preview information
  const fetchUpgradePreview = async (subscriptionId: string): Promise<void> => {
    setUpgradeLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/subscriptions/upgrade-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          subscriptionId: subscriptionId,
          targetPlan: 'premium'
        })
      });
      
      if (!response.ok) {
        const errorData: any = await response.json();
        throw new Error(errorData.error || 'Failed to fetch upgrade preview');
      }
      
      const upgradePreview = await response.json() as UpgradeInfo;
      setUpgradeInfo(upgradePreview);
      setShowUpgradeConfirmation(true);
      
    } catch (err) {
      console.error('Error fetching upgrade preview:', err);
      setError('Failed to get upgrade information. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Confirm and execute the upgrade
  const confirmUpgrade = async (): Promise<void> => {
    if (!upgradeInfo) {
      return Promise.reject(new Error('No upgrade information available'));
    }
    
    setUpgradeLoading(true);
    
    try {
      // Execute the original upgrade logic
      await upgradeSubscription(upgradeInfo.subscriptionId);
      
      // Close the confirmation modal on success
      setShowUpgradeConfirmation(false);
      setUpgradeInfo(null);
      
    } catch (err) {
      console.error('Error confirming upgrade:', err);
      throw err; // Re-throw to handle in UI
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Cancel the upgrade process
  const cancelUpgrade = (): void => {
    setShowUpgradeConfirmation(false);
    setUpgradeInfo(null);
    setUpgradeLoading(false);
  };

  // Wrapper function to maintain the original interface
  const upgradeSubscriptionWrapper = async (subscriptionId?: string): Promise<void> => {
    // If no subscriptionId provided, use the first available subscription
    const targetId = subscriptionId || (subscriptions.length > 0 ? subscriptions[0].id : undefined);
    
    if (!targetId) {
      return Promise.reject(new Error('No subscription ID available for upgrade'));
    }
    
    return fetchUpgradePreview(targetId);
  };

  // Original upgrade subscription function - now used internally
  const upgradeSubscription = async (subscriptionId?: string): Promise<void> => {
    if (!subscriptionId && subscriptions.length > 0) {
      subscriptionId = subscriptions[0].id;
    }
    
    if (!subscriptionId) {
      console.error('No subscription ID provided for upgrade');
      return Promise.reject(new Error('No subscription ID provided'));
    }
    
    try {
      // Call the API to upgrade the subscription
      const response = await fetch(`/api/subscriptions/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          plan: 'premium',
          subscriptionId: subscriptionId
        })
      });
      
      if (!response.ok) {
        const errorData: any = await response.json();
        throw new Error(errorData.error || 'Failed to upgrade subscription');
      }
      
      interface UpgradeResult {
        id: string;
        status: 'active' | 'requires_payment';
        plan: 'premium';
        price: number;
        clientSecret?: string;
        upgradeType: 'one_time' | 'recurring';
        prorationAmount?: number;
        // Add upgrade-specific fields
        upgradeAmount?: number;
        remainingDeliveries?: number;
        fromPlan?: string;
        toPlan?: string;
      }
      
      const upgradeResult = await response.json() as UpgradeResult;
      
      // Handle different upgrade types
      if (upgradeResult.upgradeType === 'one_time' && upgradeResult.clientSecret) {
        // For one-time payments, redirect to payment confirmation
        // Store payment information in localStorage to pass to payment page
        
        // Get current subscription to include service type
        const currentSubscription = subscriptions.find(sub => sub.id === subscriptionId);
        
        const paymentInfo = {
          clientSecret: upgradeResult.clientSecret,
          subscriptionId: subscriptionId,
          paymentType: 'upgrade',
          plan: 'premium',
          amount: upgradeResult.upgradeAmount || upgradeResult.price, // Use upgradeAmount if available, otherwise fallback to price
          serviceType: currentSubscription?.serviceType || 'flower',
          isUpgrade: true,
          upgradeAmount: upgradeResult.upgradeAmount,
          remainingDeliveries: upgradeResult.remainingDeliveries,
          fromPlan: upgradeResult.fromPlan,
          toPlan: upgradeResult.toPlan,
          currency: 'thb' // Default currency, could be enhanced to support USD in the future
        };
        localStorage.setItem('paymentInfo', JSON.stringify(paymentInfo));
        
        // Navigate to payment page
        window.location.href = `/stripe-payment?type=upgrade&subscription_id=${subscriptionId}`;
      } else if (upgradeResult.upgradeType === 'recurring' && upgradeResult.status === 'active') {
        // For recurring subscriptions, the upgrade is immediate
        // Update local state
        setSubscriptions(prevSubscriptions => {
          return prevSubscriptions.map(sub => {
            if (sub.id === subscriptionId) {
              return {
                ...sub,
                plan: 'premium',
                price: upgradeResult.price
              } as SubscriptionState;
            }
            return sub;
          });
        });
        
        // Refresh subscription data
        await fetchSubscriptionData(subscriptionId, true);
      } else {
        throw new Error('Unexpected upgrade response format');
      }
      
      return Promise.resolve();
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      setError('Failed to upgrade subscription. Please try again.');
      return Promise.reject(err);
    }
  };

  // Skip a specific delivery
  const skipDelivery = (deliveryDate: string, subscriptionId?: string) => {
    // Implementation depends on how you're tracking skipped deliveries
    // This is a placeholder implementation
    console.log(`Skipping delivery on ${deliveryDate} for subscription ${subscriptionId || 'default'}`);
  };

  // Update delivery schedule for a subscription
  const updateDeliverySchedule = (schedule: Schedule, subscriptionId?: string) => {
    console.log(`SubscriptionContext: Updating schedule for subscription ${subscriptionId} to`, schedule);
    
    setSubscriptions(prevSubscriptions => {
      // If no ID is provided, update the first subscription
      if (!subscriptionId && prevSubscriptions.length > 0) {
        subscriptionId = prevSubscriptions[0].id;
      }
      
      // Find the subscription to update
      return prevSubscriptions.map(sub => {
        if (sub.id === subscriptionId) {
          console.log(`SubscriptionContext: Found subscription ${sub.id} to update schedule`);
          
          // Create new deliverySettings object
          const updatedDeliverySettings: DeliverySettings = {
            time_slots: schedule.timeSlots,
            day_of_week: typeof schedule.dayOfWeek === 'string' ? 
              parseInt(schedule.dayOfWeek) : schedule.dayOfWeek
          };
          
          return {
            ...sub,
            deliverySettings: updatedDeliverySettings
          } as SubscriptionState;
        }
        return sub;
      });
    });
  };

  // Update address for a subscription
  const updateAddress = (address: Address, subscriptionId?: string) => {
    setSubscriptions(prevSubscriptions => {
      // If no ID is provided, update the first subscription
      if (!subscriptionId && prevSubscriptions.length > 0) {
        subscriptionId = prevSubscriptions[0].id;
      }
      
      // Find the subscription to update
      return prevSubscriptions.map(sub => {
        if (sub.id === subscriptionId) {
          return {
            ...sub,
            address
          } as SubscriptionState;
        }
        return sub;
      });
    });
  };

  // Add a function to fetch subscription data with force refresh option
  const fetchSubscriptionData = async (subscriptionId?: string, forceRefresh: boolean = false) => {
    console.log('SubscriptionContext: Fetching specific subscription data', subscriptionId, forceRefresh ? '(forced refresh)' : '');
    
    // If using mock data, return mock data immediately
    if (USE_MOCK_DATA) {
      console.log('SubscriptionContext: Using mock data for fetchSubscriptionData');
      if (subscriptionId) {
        const mockSubscription = MOCK_SUBSCRIPTIONS_DETAILED.find(sub => sub.id === subscriptionId);
        if (mockSubscription) {
          console.log('SubscriptionContext: Found mock subscription:', mockSubscription.id);
        } else {
          console.log('SubscriptionContext: Mock subscription not found:', subscriptionId);
        }
      }
      setSubscriptions(MOCK_SUBSCRIPTIONS_DETAILED);
      setHasFetched(true);
      setIsLoading(false);
      return;
    }
    
    if (fetchInProgressRef.current && !forceRefresh) {
      console.log('SubscriptionContext: Fetch already in progress, skipping');
      return;
    }
    
    // If we already have the subscription data and it's not a forced refresh, return
    if (!forceRefresh && hasFetched) {
      if (subscriptionId) {
        const existingSubscription = subscriptions.find(sub => sub.id === subscriptionId);
        if (existingSubscription) {
          console.log('SubscriptionContext: Already have subscription data, skipping fetch');
          return;
        }
      } else {
        // We already have all subscriptions and no specific ID was requested
        console.log('SubscriptionContext: Already have all subscription data, skipping fetch');
        return;
      }
    }
    
    fetchInProgressRef.current = true;
    setIsLoading(true);
    
    try {
      if (forceRefresh && subscriptionId) {
        console.log('🔄 BYPASSING NORMAL API CLIENT - USING DIRECT FETCH WITH NO_CACHE');
        
        // Get auth token
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('No auth token found');
        }
        
        // Make direct fetch request with cache control
        const response = await fetch(`/api/subscriptions/${subscriptionId}?_t=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
            'Pragma': 'no-cache',
            'Expires': '0',
            'X-Force-Fresh': 'true'
          },
          cache: 'no-store'
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch subscription: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('👉 Direct fetch response:', data);
        
        // Now fetch all subscriptions normally to update the state
        await fetchSubscriptions(true);
      } else {
        await fetchSubscriptions(forceRefresh);
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
    } finally {
      setIsLoading(false);
      fetchInProgressRef.current = false;
    }
  };

  const value = {
    subscription: subscriptions[0] || null,
    subscriptions,
    isLoading,
    loading: isLoading,
    error,
    subscribe,
    renewSubscription,
    upgradeSubscription: upgradeSubscriptionWrapper, // Use the wrapper function
    skipDelivery,
    updateDeliverySchedule,
    updateAddress,
    fetchSubscriptionData,
    // Upgrade confirmation related
    upgradeInfo,
    showUpgradeConfirmation,
    upgradeLoading,
    fetchUpgradePreview,
    confirmUpgrade,
    cancelUpgrade,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <UpgradeConfirmationModal
        isOpen={showUpgradeConfirmation}
        onClose={cancelUpgrade}
        onConfirm={confirmUpgrade}
        upgradeInfo={upgradeInfo}
        loading={upgradeLoading}
      />
    </SubscriptionContext.Provider>
  );
}; 